import * as awarenessProtocol from 'y-protocols/awareness';
import * as Y from 'yjs';

import { getPB, getSession } from '../client';
import {
  getYdocWorkspacePbId,
  insertFileYdocUpdate,
  loadFileYdocSnapshot,
  markCollabActive,
  markCollabInactive,
  upsertFileYdocSnapshot,
} from './db';
import { base64ToUint8, uint8ToBase64 } from './encoding';
import {
  readLegacyLocalStorage,
  readLocalFileYdoc,
  writeLocalFileYdoc,
} from './local';

export type CollabUser = {
  id: string;
  displayName?: string;
  email?: string;
  avatarUrl?: string | null;
  color?: string;
};

export type CollabPresencePeer = {
  key: string;
  clientId: number;
  id: string | null;
  name: string;
  color: string;
  avatarUrl: string | null;
};

const PERSIST_TIMEOUT_MS = 45_000;
const PERSIST_RETRY_BASE_MS = 2000;
const PERSIST_RETRY_MAX_MS = 30_000;
const ARCHIVE_MIN_INTERVAL_MS = 15 * 60_000;
/** Realtime subscribe must not block opening a note forever. */
const SUBSCRIBE_TIMEOUT_MS = 8_000;
const LOAD_DB_TIMEOUT_MS = 8_000;

const COLORS = [
  '#c9a84c',
  '#5a8fc8',
  '#c85050',
  '#5a9a5a',
  '#d4903a',
  '#9b6b9e',
  '#4ecdc4',
];

function pickColor(id = ''): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash + id.charCodeAt(i) * 17) % COLORS.length;
  }
  return COLORS[Math.abs(hash) % COLORS.length]!;
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label = 'Operation',
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export type CollabStatus =
  | 'connecting'
  | 'saved'
  | 'saving'
  | 'offline'
  | 'disconnected'
  | 'error';

/**
 * Per-file Yjs provider: PocketBase snapshot/updates + collection realtime.
 * Class name kept for import stability after the Supabase → PocketBase cutover.
 */
export class SupabaseFileYjsProvider {
  doc: Y.Doc;
  workspaceId: string;
  filePath: string;
  user: CollabUser | null;
  readOnly: boolean;
  persistSnapshots: boolean;
  onStatus: (status: CollabStatus) => void;
  awareness: awarenessProtocol.Awareness;

  private connected = false;
  private _saving = false;
  private _persistPromise: Promise<void> | null = null;
  private _persistTimer: ReturnType<typeof setTimeout> | null = null;
  private _persistAgain = false;
  private _persistError = false;
  private _persistFailCount = 0;
  private _dirtyGen = 0;
  private _localTimer: ReturnType<typeof setTimeout> | null = null;
  private _awarenessTimer: ReturnType<typeof setTimeout> | null = null;
  private _destroyed = false;
  private _loadedFromServer = false;
  /** Latest durable Yjs activity from PocketBase, or null if never loaded. */
  private _serverUpdatedAtMs: number | null = null;
  private _dirty = false;
  private _lastArchiveAt = 0;
  private _skipNextArchive = false;
  private _lastPersistedB64: string | null = null;
  private _reconnecting = false;
  private _subscribeAttempted = false;
  private _presenceListeners = new Set<(peers: CollabPresencePeer[]) => void>();
  private _presencePeers: CollabPresencePeer[] | null = null;
  private _pendingAwarenessClients = new Set<number>();
  private _status: CollabStatus | null = null;
  private _everConnected = false;
  private _applyingRemote = false;
  private _pendingSync: Uint8Array | null = null;
  private _syncFlushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Coalesced updates waiting for a durable PocketBase insert (not broadcast). */
  private _pendingPersistSync: Uint8Array | null = null;
  private _syncPersistTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastEncodedSize = 0;
  private _updateHandler: (update: Uint8Array, origin: unknown) => void;
  private _awarenessHandler: (
    changes: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ) => void;
  private _onOnline: (() => void) | null = null;
  private _onOffline: (() => void) | null = null;
  private _onPageHide: (() => void) | null = null;
  private _onVisibility: (() => void) | null = null;
  private _unsubUpdates: (() => void) | null = null;
  private _workspacePbId: string | null = null;
  private _clientKey: string;
  /** Recently published update payloads to ignore echo from our own subscribe. */
  private _recentSentB64 = new Set<string>();

  constructor(
    doc: Y.Doc,
    workspaceId: string,
    filePath: string,
    options: {
      user?: CollabUser | null;
      onStatus?: (status: CollabStatus) => void;
      readOnly?: boolean;
      persistSnapshots?: boolean;
    } = {},
  ) {
    this.doc = doc;
    this.workspaceId = workspaceId;
    this.filePath = filePath;
    this.user = options.user ?? null;
    this.readOnly = options.readOnly ?? false;
    this.persistSnapshots = options.persistSnapshots ?? true;
    this.onStatus = options.onStatus ?? (() => {});
    this.awareness = new awarenessProtocol.Awareness(doc);
    this._clientKey = `${doc.clientID}:${Math.random().toString(36).slice(2, 8)}`;

    if (this.user) this._setAwarenessUser(this.user);

    this._updateHandler = (update, origin) => {
      if (this._destroyed) return;
      this._scheduleLocalSave();
      if (origin === this || this.readOnly || this._applyingRemote) return;
      this._markDirty();
      this._scheduleSyncUpdate(update);
      this._schedulePersist();
      this._emitStatus();
    };
    this.doc.on('update', this._updateHandler);

    this._awarenessHandler = ({ added, updated, removed }, origin) => {
      this._syncPresenceFromAwareness();
      if (origin === this) {
        if (added.length > 0) {
          this._scheduleAwarenessSend([this.awareness.clientID]);
        }
        return;
      }
      const changed = added.concat(updated, removed);
      if (!changed.length) return;
      this._scheduleAwarenessSend(changed);
    };
    this.awareness.on('update', this._awarenessHandler);
  }

  getXmlFragment(name = 'prosemirror'): Y.XmlFragment {
    return this.doc.getXmlFragment(name);
  }

  /** True when local edits have not been confirmed in a cloud snapshot yet. */
  isDirty(): boolean {
    return this._dirty;
  }

  hasLoadedFromServer(): boolean {
    return this._loadedFromServer;
  }

  /**
   * Latest durable Yjs activity timestamp from PocketBase (`null` when the
   * server load failed or no snapshot/updates exist yet).
   */
  getServerUpdatedAtMs(): number | null {
    return this._serverUpdatedAtMs;
  }

  /**
   * Flush pending sync updates and snapshot to PocketBase. Call before
   * {@link destroy} so another origin does not reopen stale Yjs while
   * Markdown already contains the latest save.
   */
  async flushPersist(): Promise<void> {
    if (this._destroyed || this.readOnly || !this.persistSnapshots) return;
    if (this._persistTimer) {
      clearTimeout(this._persistTimer);
      this._persistTimer = null;
    }
    await this._flushSyncBroadcast();
    await this._flushSyncPersist();
    if (this._dirty || this._persistAgain) {
      await this._persistSnapshot();
    }
  }

  onPresence(fn: (peers: CollabPresencePeer[]) => void): () => void {
    this._presenceListeners.add(fn);
    if (this._presencePeers) fn(this._presencePeers);
    return () => {
      this._presenceListeners.delete(fn);
    };
  }

  _setAwarenessUser(user: CollabUser) {
    const color = user.color || pickColor(user.id);
    this.user = { ...user, color };
    this.awareness.setLocalStateField('user', {
      name: user.email || user.displayName || 'Collaborator',
      color,
      id: user.id,
      avatarUrl: user.avatarUrl || null,
    });
    this._syncPresenceFromAwareness();
  }

  private _setStatus(next: CollabStatus) {
    if (this._destroyed) return;
    if (this._status === next) return;
    this._status = next;
    this.onStatus(next);
  }

  private _emitStatus() {
    if (this._destroyed) return;
    if (!this._isOnline()) {
      this._setStatus('offline');
      return;
    }
    if (this._reconnecting) {
      this._setStatus('connecting');
      return;
    }
    if (!this.connected) {
      if (this._loadedFromServer && !this._subscribeAttempted) {
        if (!this.readOnly && this.persistSnapshots && this._saving) {
          this._setStatus('saving');
        } else {
          this._setStatus('saved');
        }
        return;
      }
      this._setStatus(
        this._everConnected || this._subscribeAttempted
          ? 'disconnected'
          : 'connecting',
      );
      return;
    }
    if (!this._loadedFromServer) {
      this._setStatus('error');
      return;
    }
    if (
      !this.readOnly &&
      this.persistSnapshots &&
      this._persistError &&
      this._dirty &&
      !this._saving
    ) {
      this._setStatus('error');
      return;
    }
    if (!this.readOnly && this.persistSnapshots && this._saving) {
      this._setStatus('saving');
      return;
    }
    this._setStatus('saved');
  }

  private _markDirty() {
    if (!this.persistSnapshots || this.readOnly) return;
    this._dirty = true;
    this._dirtyGen += 1;
    this._persistError = false;
  }

  async connect() {
    markCollabActive(this.workspaceId, this.filePath);
    this._setStatus('connecting');
    this._bindWindowListeners();
    await this._loadFromLocal();

    if (this._isOnline()) {
      try {
        await withTimeout(this._loadFromDb(), LOAD_DB_TIMEOUT_MS, 'Yjs load');
      } catch (e) {
        // Markdown body still opens from workspace_files; don't hard-fail the
        // editor when collab history is slow/unavailable after cutover.
        console.warn('Failed to load yjs from server', e);
        this._loadedFromServer = true;
        this._subscribeAttempted = true;
      }
    } else {
      // Offline: treat local/legacy as good enough to edit.
      this._loadedFromServer = true;
    }

    if (this._destroyed) return;

    this.doc.getMap('docComments');
    this._saveLocal();
    this._emitStatus();
    void this._startLiveSync();
  }

  private async _startLiveSync() {
    if (this._destroyed || !this._isOnline()) return;
    try {
      await this._subscribeStreams();
      if (this._destroyed) return;
      if (!this.readOnly && this._loadedFromServer && this._dirty) {
        await this._persistSnapshot();
      }
    } catch (e) {
      console.warn('Failed to start yjs live sync', e);
      this._subscribeAttempted = true;
    }
    if (!this._destroyed) this._emitStatus();
  }

  private _isOnline() {
    return typeof navigator === 'undefined' || navigator.onLine;
  }

  private _bindWindowListeners() {
    if (typeof window === 'undefined') return;
    this._onOnline = () => {
      void this._handleOnline();
    };
    this._onOffline = () => this._handleOffline();
    this._onPageHide = () => {
      this._saveLocal();
      this._announceLeave();
    };
    this._onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        this._saveLocal();
        return;
      }
      if (this._isOnline() && !this.connected && !this._reconnecting) {
        void this._handleOnline();
      }
      if (this._dirty && this._loadedFromServer && this._isOnline()) {
        void this._persistSnapshot();
      }
    };
    window.addEventListener('online', this._onOnline);
    window.addEventListener('offline', this._onOffline);
    window.addEventListener('pagehide', this._onPageHide);
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  private _handleOffline() {
    this._teardownStreams();
    if (this._syncFlushTimer) clearTimeout(this._syncFlushTimer);
    this._syncFlushTimer = null;
    if (this._syncPersistTimer) clearTimeout(this._syncPersistTimer);
    this._syncPersistTimer = null;
    if (this._persistTimer) clearTimeout(this._persistTimer);
    this._persistTimer = null;
    this._saveLocal();
    this._emitStatus();
  }

  private async _handleOnline() {
    if (this._destroyed || this._reconnecting) return;
    this._reconnecting = true;
    this._setStatus('connecting');
    try {
      await withTimeout(this._loadFromDb(), LOAD_DB_TIMEOUT_MS, 'Yjs load');
      this._saveLocal();
      await this._subscribeStreams();
      await this._flushSyncBroadcast();
      await this._flushSyncPersist();
      if (!this.readOnly && this._loadedFromServer && this._dirty) {
        await this._persistSnapshot();
      }
    } catch (e) {
      console.warn('Failed to sync yjs after reconnect', e);
      this._subscribeAttempted = true;
    } finally {
      this._reconnecting = false;
      this._emitStatus();
    }
  }

  private async _subscribeStreams() {
    this._teardownStreams();
    const session = await getSession();
    if (!session?.access_token) {
      this._subscribeAttempted = true;
      return;
    }

    try {
      this._workspacePbId = await getYdocWorkspacePbId(this.workspaceId);
    } catch (e) {
      console.warn('Failed to resolve workspace for yjs subscribe', e);
      this._subscribeAttempted = true;
      return;
    }

    if (this._destroyed) return;

    const pb = getPB();
    const wsPbId = this._workspacePbId;
    const filePath = this.filePath;

    try {
      const unsub = await withTimeout(
        pb.collection('workspace_file_ydoc_updates').subscribe(
          '*',
          (e) => {
            if (e.action !== 'create') return;
            const record = e.record;
            if (!record) return;
            if (String(record.file_path ?? '') !== filePath) return;
            const updateB64 = String(record.update_b64 ?? '');
            if (!updateB64) return;
            if (this._recentSentB64.has(updateB64)) return;
            const kind = String(record.kind ?? 'sync');
            if (kind === 'awareness') {
              this._applyRemoteAwareness({ updateB64 });
            } else {
              this._applyRemoteSync({ updateB64 });
            }
          },
          {
            filter: `workspace = "${wsPbId}" && file_path = "${filePath.replace(/"/g, '\\"')}"`,
          },
        ),
        SUBSCRIBE_TIMEOUT_MS,
        'Realtime subscribe',
      );
      this._unsubUpdates = unsub;
      this.connected = true;
    } catch (e) {
      console.warn('Yjs PocketBase subscribe failed', e);
      this.connected = false;
      this._teardownStreams();
    }

    this._subscribeAttempted = true;
    this._everConnected = this._everConnected || this.connected;
    this._emitStatus();
    if (this.connected) this._scheduleAwarenessSend([this.doc.clientID]);
  }

  private _teardownStreams() {
    if (this._unsubUpdates) {
      try {
        this._unsubUpdates();
      } catch {
        // ignore
      }
      this._unsubUpdates = null;
    }
    this.connected = false;
  }

  private _applyRemoteSync(payload: { updateB64?: string }) {
    if (!payload?.updateB64) return;
    const bytes = base64ToUint8(payload.updateB64);
    if (!bytes?.length) return;
    this._applyingRemote = true;
    try {
      Y.applyUpdate(this.doc, bytes, this);
    } finally {
      this._applyingRemote = false;
    }
  }

  private _applyRemoteAwareness(payload: { updateB64?: string }) {
    if (!payload?.updateB64) return;
    const bytes = base64ToUint8(payload.updateB64);
    if (!bytes?.length) return;
    awarenessProtocol.applyAwarenessUpdate(this.awareness, bytes, this);
    this._syncPresenceFromAwareness();
  }

  private async _loadFromLocal() {
    let local = await readLocalFileYdoc(this.workspaceId, this.filePath);
    if (!local) {
      local = readLegacyLocalStorage(this.workspaceId, this.filePath);
    }
    if (!local) return;
    Y.applyUpdate(this.doc, local.bytes, this);
    if (local.dirty && this.persistSnapshots && !this.readOnly) {
      this._dirty = true;
      this._dirtyGen += 1;
    }
  }

  private async _loadFromDb() {
    const { snapshotB64, updates, updatedAtMs, workspacePbId } =
      await loadFileYdocSnapshot(this.workspaceId, this.filePath);
    this._workspacePbId = workspacePbId;
    const snapshot = base64ToUint8(snapshotB64);
    if (snapshot?.length) Y.applyUpdate(this.doc, snapshot, this);
    if (snapshotB64) this._lastPersistedB64 = snapshotB64;

    for (const u of updates) {
      const bytes = base64ToUint8(u.update_b64);
      if (bytes?.length) Y.applyUpdate(this.doc, bytes, this);
    }

    this._serverUpdatedAtMs =
      updatedAtMs != null && !Number.isNaN(updatedAtMs) ? updatedAtMs : null;
    this._loadedFromServer = true;
  }

  private _localSaveDelay() {
    const size = this._lastEncodedSize || 0;
    if (size > 2_000_000) return 4000;
    if (size > 500_000) return 2000;
    return 800;
  }

  private _persistDelay() {
    const size = this._lastEncodedSize || 0;
    if (size > 2_000_000) return 12000;
    if (size > 500_000) return 6000;
    return 2500;
  }

  private _scheduleLocalSave() {
    if (this._localTimer) clearTimeout(this._localTimer);
    const delay = this._isOnline() ? this._localSaveDelay() : 50;
    this._localTimer = setTimeout(() => this._saveLocal(), delay);
  }

  private _saveLocal(opts?: { force?: boolean }) {
    if (this._destroyed && !opts?.force) return;
    if (this._localTimer) clearTimeout(this._localTimer);
    this._localTimer = null;
    const bytes = Y.encodeStateAsUpdate(this.doc);
    this._lastEncodedSize = bytes?.length || 0;
    void writeLocalFileYdoc(
      this.workspaceId,
      this.filePath,
      bytes,
      this._dirty,
    );
  }

  private _schedulePersist(delay: number | null = null) {
    if (!this.persistSnapshots || this.readOnly) return;
    if (!this._loadedFromServer || !this._isOnline()) return;
    if (!this._dirty && !this._persistAgain) return;
    if (this._persistTimer) clearTimeout(this._persistTimer);
    const wait = delay == null ? this._persistDelay() : delay;
    this._persistTimer = setTimeout(() => {
      this._persistTimer = null;
      void this._persistSnapshot();
    }, wait);
  }

  private _persistRetryDelay() {
    return Math.min(
      PERSIST_RETRY_MAX_MS,
      PERSIST_RETRY_BASE_MS * 2 ** Math.min(this._persistFailCount, 4),
    );
  }

  private _scheduleSyncUpdate(update: Uint8Array) {
    if (!update?.length) return;
    this._pendingSync = this._pendingSync
      ? Y.mergeUpdates([this._pendingSync, update])
      : update;
    this._pendingPersistSync = this._pendingPersistSync
      ? Y.mergeUpdates([this._pendingPersistSync, update])
      : update;
    if (!this._isOnline()) return;
    if (!this._syncFlushTimer) {
      this._syncFlushTimer = setTimeout(() => {
        this._syncFlushTimer = null;
        void this._flushSyncBroadcast();
      }, 120);
    }
    if (!this._syncPersistTimer) {
      this._syncPersistTimer = setTimeout(() => {
        this._syncPersistTimer = null;
        void this._flushSyncPersist();
      }, this._persistDelay());
    }
  }

  private async _flushSyncBroadcast() {
    if (this._syncFlushTimer) clearTimeout(this._syncFlushTimer);
    this._syncFlushTimer = null;
    const bytes = this._pendingSync;
    this._pendingSync = null;
    // One insert serves both live peers (PB subscribe) and durable sync rows.
    this._pendingPersistSync = null;
    if (this._syncPersistTimer) {
      clearTimeout(this._syncPersistTimer);
      this._syncPersistTimer = null;
    }
    if (!bytes?.length) return;
    await this._persistSyncUpdate(uint8ToBase64(bytes), 'sync');
  }

  private async _flushSyncPersist() {
    if (this._syncPersistTimer) clearTimeout(this._syncPersistTimer);
    this._syncPersistTimer = null;
    const bytes = this._pendingPersistSync;
    this._pendingPersistSync = null;
    if (!bytes?.length) return;
    // Fallback when broadcast flush was skipped (e.g. offline → reconnect).
    await this._persistSyncUpdate(uint8ToBase64(bytes), 'sync');
  }

  private async _persistSyncUpdate(
    updateB64: string,
    kind: 'sync' | 'awareness',
  ) {
    if (!this._loadedFromServer || !this._isOnline() || this.readOnly) return;
    this._recentSentB64.add(updateB64);
    if (this._recentSentB64.size > 40) {
      const first = this._recentSentB64.values().next().value;
      if (first) this._recentSentB64.delete(first);
    }
    try {
      await insertFileYdocUpdate({
        workspaceId: this.workspaceId,
        filePath: this.filePath,
        updateB64,
        kind,
        clientId: this._clientKey,
      });
    } catch (e) {
      console.warn('Failed to persist yjs update', e);
    }
  }

  private _scheduleAwarenessSend(clientIds: number[]) {
    for (const id of clientIds) this._pendingAwarenessClients.add(id);
    if (this._awarenessTimer) clearTimeout(this._awarenessTimer);
    this._awarenessTimer = setTimeout(() => {
      const pending = Array.from(this._pendingAwarenessClients);
      this._pendingAwarenessClients.clear();
      if (!pending.length) return;
      try {
        const bytes = awarenessProtocol.encodeAwarenessUpdate(
          this.awareness,
          pending,
        );
        void this._persistSyncUpdate(uint8ToBase64(bytes), 'awareness');
        this._syncPresenceFromAwareness();
      } catch (e) {
        console.warn('Failed to encode awareness', e);
      }
    }, 200);
  }

  private _syncPresenceFromAwareness() {
    const byUser = new Map<string, CollabPresencePeer>();
    const localClientId = this.awareness.clientID;
    const localUserId = this.user?.id ?? null;
    this.awareness.getStates().forEach((state, clientId) => {
      if (clientId === localClientId) return;
      const u = state?.user as
        | {
            id?: string;
            name?: string;
            color?: string;
            avatarUrl?: string | null;
          }
        | undefined;
      if (!u) return;
      if (localUserId && u.id && u.id === localUserId) return;
      const id = u.id || `client-${clientId}`;
      byUser.set(id, {
        key: id,
        clientId,
        id: u.id || null,
        name: u.name || 'Collaborator',
        color: u.color || pickColor(id),
        avatarUrl: u.avatarUrl || null,
      });
    });
    const next = Array.from(byUser.values());
    const prev = this._presencePeers;
    if (
      prev &&
      prev.length === next.length &&
      prev.every(
        (peer, i) =>
          peer.key === next[i]?.key && peer.clientId === next[i]?.clientId,
      )
    ) {
      return;
    }
    this._presencePeers = next;
    this._presenceListeners.forEach((fn) => {
      fn(next);
    });
  }

  private async _persistSnapshot() {
    if (
      this._destroyed ||
      this.readOnly ||
      !this.persistSnapshots ||
      !this._loadedFromServer
    ) {
      return;
    }
    if (!this._dirty && !this._persistAgain) return;
    if (this._saving) {
      this._persistAgain = true;
      return this._persistPromise ?? undefined;
    }

    this._saving = true;
    this._persistAgain = false;
    const gen = this._dirtyGen;
    this._emitStatus();
    this._persistPromise = (async () => {
      try {
        await this._flushSyncBroadcast();
        await this._flushSyncPersist();
        const shouldArchive =
          !this._skipNextArchive &&
          (!this._lastArchiveAt ||
            Date.now() - this._lastArchiveAt >= ARCHIVE_MIN_INTERVAL_MS);
        this._skipNextArchive = false;
        await withTimeout(
          (async () => {
            const snapshot = Y.encodeStateAsUpdate(this.doc);
            const snapshotB64 = uint8ToBase64(snapshot);
            await upsertFileYdocSnapshot({
              workspaceId: this.workspaceId,
              filePath: this.filePath,
              snapshotB64,
              archivePrevious: shouldArchive,
              previousSnapshotB64: shouldArchive
                ? this._lastPersistedB64
                : null,
            });
            this._lastPersistedB64 = snapshotB64;
            this._serverUpdatedAtMs = Date.now();
          })(),
          PERSIST_TIMEOUT_MS,
          'Snapshot save',
        );
        if (shouldArchive) this._lastArchiveAt = Date.now();
        if (this._dirtyGen === gen) this._dirty = false;
        this._persistError = false;
        this._persistFailCount = 0;
        this._saveLocal();
      } catch (e) {
        console.warn('Failed to persist yjs snapshot', e);
        this._persistError = true;
        this._persistFailCount += 1;
        this._dirty = true;
      } finally {
        this._saving = false;
        this._persistPromise = null;
        const again = this._persistAgain || this._dirty;
        this._persistAgain = false;
        if (!this._destroyed && again && this._isOnline()) {
          if (!this._dirty) this._persistAgain = true;
          this._schedulePersist(
            this._persistError
              ? this._persistRetryDelay()
              : this._persistDelay(),
          );
        }
        this._emitStatus();
      }
    })();
    return this._persistPromise;
  }

  private _announceLeave() {
    const clientId = this.doc.clientID;
    awarenessProtocol.removeAwarenessStates(
      this.awareness,
      [clientId],
      'local',
    );
    this._syncPresenceFromAwareness();
    if (this._awarenessTimer) clearTimeout(this._awarenessTimer);
    this._awarenessTimer = null;
    this._pendingAwarenessClients.clear();
    try {
      const bytes = awarenessProtocol.encodeAwarenessUpdate(this.awareness, [
        clientId,
      ]);
      void this._persistSyncUpdate(uint8ToBase64(bytes), 'awareness');
    } catch {
      // ignore
    }
  }

  /**
   * Tear down the provider. Pass `{ persist: false }` when the note was
   * deleted or replaced at the same path — otherwise pending flush/local
   * saves resurrect path-keyed ydoc after storage already purged it.
   */
  destroy(options?: { persist?: boolean }) {
    if (this._destroyed) return;
    this._destroyed = true;
    this.onStatus = () => {};
    markCollabInactive(this.workspaceId, this.filePath);
    if (this._persistTimer) clearTimeout(this._persistTimer);
    if (this._localTimer) clearTimeout(this._localTimer);
    if (this._awarenessTimer) clearTimeout(this._awarenessTimer);
    if (this._syncFlushTimer) clearTimeout(this._syncFlushTimer);
    if (this._syncPersistTimer) clearTimeout(this._syncPersistTimer);
    this._pendingAwarenessClients.clear();
    const persist = options?.persist !== false;
    if (persist) {
      if (this._pendingSync?.length) {
        void this._flushSyncBroadcast();
      }
      if (this._pendingPersistSync?.length) {
        void this._flushSyncPersist();
      }
      this._saveLocal({ force: true });
    } else {
      this._pendingSync = null;
      this._pendingPersistSync = null;
    }
    if (typeof window !== 'undefined') {
      if (this._onOnline) window.removeEventListener('online', this._onOnline);
      if (this._onOffline)
        window.removeEventListener('offline', this._onOffline);
      if (this._onPageHide)
        window.removeEventListener('pagehide', this._onPageHide);
      if (this._onVisibility)
        document.removeEventListener('visibilitychange', this._onVisibility);
    }
    this.doc.off('update', this._updateHandler);
    this.awareness.off('update', this._awarenessHandler);
    this._announceLeave();
    this._teardownStreams();
    this.awareness.destroy();
  }
}
